import queryString from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';

import ListView from '../../../base/ListView';
import { useEventMaterials } from '../../../hooks/api/event-material/useEventMaterials';
import { useAllMaterialRequests } from '../../../hooks/api/material-requests/useAllMaterialRequests';
import { useApproveMaterialRequest } from '../../../hooks/api/material-requests/useApproveMaterialRequest';
import { useCompanyMaterialRequests } from '../../../hooks/api/material-requests/useCompanyMaterialRequests';
import { useDeleteMaterialRequest } from '../../../hooks/api/material-requests/useDeleteMaterialRequest';
import { useRejectMaterialRequest } from '../../../hooks/api/material-requests/useRejectMaterialRequest';
import { useMaterials } from '../../../hooks/api/materials/useMaterials';
import { MaterialRequest } from '../../../hooks/api/utils/types';
import { Event } from '../../../hooks/types';
import { readableDateTime } from '../../../libs/date';
import { defaultError } from '../../../libs/utils';
import { selectCompanyRegistration } from '../../../redux/preferences/selectors';
import { getCompanies } from '../../../services/api/companies';
import CreateMaterialRequestButton from './CreateMaterialRequestButton';
import { EditMaterialRequestDaysButton } from './EditMaterialRequestDaysButton';
import MaterialRequestDayButton from './MaterialRequestDaysButton';

export type MaterialRequestOverviewProps = {
  admin: boolean | undefined;
  event: Event;
};

const todo = (...args: any[]) => {
  console.log('uh', args);
};

const getIncludes = (admin: boolean) => {
  if (admin) {
    return 'material,days,registration,registration.company';
  } else {
    return 'material,days';
  }
};

export const MaterialRequestOverviewNew = ({ admin, event, ...props }: MaterialRequestOverviewProps) => {
  const [additionalFilters, setAdditionalFilters] = useState<Record<string, any>>({});

  const [listFilters, setListFilters] = useState<Record<string, any>>({});
  const registration = useSelector(selectCompanyRegistration);

  const baseFilters = {
    'filter[event_id]': event.id,
    'include': admin ? 'material,days,registration,registration.company' : 'material,days',
  };
  const parsedFilters = queryString.parse(window.location.search);
  const materialRequestQuery = useAllMaterialRequests(event.id, {
    ...baseFilters,
    ...parsedFilters,
    ...listFilters,
    enabled: admin ?? false,
    include: getIncludes(admin ?? false),
  });

  const companyMaterialRequestQuery = useCompanyMaterialRequests(event.id, registration?.id, {
    ...baseFilters,
    ...parsedFilters,
    ...listFilters,
    enabled: !admin,
    include: getIncludes(admin ?? false),
  });

  const {
    data: materialRequests,
    isLoading: isLoadingMaterialRequests,
    refetch,
  } = admin ? materialRequestQuery : companyMaterialRequestQuery;

  useEffect(() => {
    refetch();
  }, [listFilters]);

  const { data: materials, isLoading: isLoadingMaterials } = useMaterials({});

  const { mutate: approve } = useApproveMaterialRequest();
  const { mutate: reject } = useRejectMaterialRequest();
  const { mutate: deleteRequest } = useDeleteMaterialRequest();

  const cols = useMemo(() => {
    if (!admin) {
      return columns;
    } else {
      return [
        ...columns,
        {
          key: 'company',
          value: 'Company',
          render: (request: MaterialRequest) => request?.registration?.company?.name,
        },
      ];
    }
  }, [admin]);

  const filters = useMemo(() => {
    if (!admin) {
      return [];
    } else {
      return [
        {
          label: 'Company',
          icon: 'la-building',
          type: 'typeahead',
          options: (value: any) =>
            getCompanies({
              'filter[name]': value,
            }),
          key: 'company_id',
          labelKey: 'name',
        },
        {
          label: 'Status',
          icon: 'la-calendar',
          type: 'select',
          key: 'current_status',
          dataset: [
            { key: 'pending', value: 'Pending' },
            { key: 'approved', value: 'Approved' },
            { key: 'rejected', value: 'Rejected' },
          ],
        },
      ];
    }
  }, [admin]);

  const daysBtn = (item: MaterialRequest) => {
    if (!admin) {
      return <EditMaterialRequestDaysButton request={item} />;
    } else {
      return (
        <>
          <EditMaterialRequestDaysButton request={item} key="days" admin />
          <span key="approvereject">
            {(item.status === 'pending' || item.status === 'rejected') && (
              <span key="approve">
                <button
                  className="btn btn-outline-success m-btn btn-sm  m-btn--icon m--margin-left-5"
                  onClick={() =>
                    approve({
                      registrationId: item.registrationId!,
                      requestId: item.id!,
                    })
                  }
                >
                  <span>
                    <i className="la la-check" />
                    <span>Approve</span>
                  </span>
                </button>
              </span>
            )}

            {(item.status === 'pending' || item.status === 'approved') && (
              <span key="reject">
                <button
                  className="btn btn-outline-danger m-btn btn-sm  m-btn--icon m--margin-left-5"
                  onClick={() =>
                    reject({
                      registrationId: item.registrationId!,
                      requestId: item.id!,
                    })
                  }
                >
                  <span>
                    <i className="la la-close" />
                    <span>Reject</span>
                  </span>
                </button>
              </span>
            )}
          </span>
        </>
      );
    }
  };

  const listViewConf = {
    dataSet: materialRequests?.data,
    dataMeta: materialRequests?.meta,
    columns: cols,
    filters,
    baseFilters: baseFilters,
    actions: {
      deleteLink: true,
      editLink: false,
      detailsLink: false,

      deleteFunction: (requestId: number) => {
        const request = materialRequests?.data.find((r) => r.id === requestId);
        if (!request) {
          return defaultError('Unable to delete: request not found.');
        }
        return deleteRequest({
          registrationId: request.registrationId!,
          requestId: request.id!,
        });
      },
      customLink: daysBtn,
    },
    url: '/admin/material-requests',
  };

  return (
    <div className="m-content">
      <div className="row">
        <div className="col-xl-12">
          <div className="m-portlet">
            <div className="m-portlet__head">
              <div className="m-portlet__head-caption">
                <div className="m-portlet__head-title" />
              </div>

              <div className="m-portlet__head-tools">
                <ul className="m-portlet__nav m--margin-left-20">
                  <li className="m-portlet__nav-item">
                    <CreateMaterialRequestButton
                      registration={registration}
                      refreshRequests={refetch}
                      materials={materials?.data ?? []}
                      admin={admin}
                      event={event}
                    />
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-12">
              {!isLoadingMaterialRequests && <ListView config={listViewConf} handleFilterUpdate={setListFilters} />}
              {isLoadingMaterialRequests && <div className="alert alert-info">Loading</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const columns = [
  {
    key: 'material',
    value: 'Material',
    render: (request: MaterialRequest) => request.material?.name,
  },
  {
    key: 'requestedOn',
    value: 'Requested On',
    render: (request: MaterialRequest) => readableDateTime(request.createdAt, 'unknown'),
  },
  {
    key: 'status',
    value: 'Status',
    render: (request: MaterialRequest) => {
      switch (request.status) {
        case 'approved':
          return <div className="m-badge m-badge--success m-badge--wide">{request.status}</div>;
        case 'rejected':
          return <div className="m-badge m-badge--danger m-badge--wide">{request.status}</div>;
        default:
          return <div className="m-badge m-badge--metal m-badge--wide">{request.status}</div>;
      }
    },
  },
];
